import React, { useState } from "react";
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Button,
  CardContent,
  Typography,
  Paper,
  TextField,
  Card,
  Grid,
  CircularProgress,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@material-ui/core";

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import BusinessIcon from '@material-ui/icons/Business';
import EmailIcon from '@material-ui/icons/Email';

import gql from 'graphql-tag';
import { useLazyQuery, useQuery } from '@apollo/client';

export const CompanyAccessReport = () => {
  const useStyles = makeStyles(theme => ({
    root: {
      padding: theme.spacing(1, 2),
    },
    fileCard: {
      width: 300,
      minHeight: 56,
      display: 'inline-block',
      marginRight: 15,
      marginBottom: 15,
      '&:hover': {
        border: `1px solid ${theme.palette.primary.main}`,
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }
    },

    // --- Dialog styles ---
    dialogTitle: {
      paddingBottom: theme.spacing(1),
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
    emailWrap: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginTop: theme.spacing(0.5),
      color: theme.palette.text.secondary
    },
    subtleCaption: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.25)
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
    },
    companyList: {
      paddingTop: 0,
    },
    companyItem: {
      borderRadius: 6,
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      }
    },
    companyIcon: {
      color: theme.palette.primary.main
    },
    emptyState: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5)
    },
    dialogContent: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    dividerTight: {
      margin: theme.spacing(1.5, 0),
    }
  }));

  const classes = useStyles();

  // --- Queries ---
  const SEARCH_USER = gql`
    query GetUserByEmail($email: String) {
      user(email: $email) {
        guid
        email
      }
    }
  `;

  const GET_USER = gql`
    query GetUser($userId: String) {
      user(guid: $userId) {
        email
      }
      userPermissions(userId: $userId) {
        state
        permission {
          id
          permissionName
        }
      }
      userCompanies(userId: $userId) {
        state
        company {
          id
          name
        }
      }
    }
  `;

  // --- Search state ---
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [userSearch, userSearchQueryResults] = useLazyQuery(SEARCH_USER, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setIsLoading(false);
      const users = data?.user ?? [];
      // Auto-open dialog after search click if there is at least one result
      if (users.length >= 1) {
        setSelectedUser(users[0].guid);
        setUserProfileIsOpen(true);
      }
    },
    onError: () => {
      setIsLoading(false);
    }
  });

  const handleUserSearch = () => {
    if (searchText && searchText.trim().length > 0) {
      setIsLoading(true);
      userSearch({ variables: { email: searchText.trim() } });
    }
  };

  const handleSearchTextChange = (event) => setSearchText(event.target.value);

  // --- Dialog state ---
  const [userProfileIsOpen, setUserProfileIsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");

  const handleUserProfileOpen = (userId) => {
    setSelectedUser(userId);
    setUserProfileIsOpen(true);
  };

  const handleDialogClose = () => setUserProfileIsOpen(false);

  // --- Detail query for selected user (for the dialog) ---
  const { data: detailData, loading: detailLoading } = useQuery(GET_USER, {
    variables: { userId: selectedUser },
    skip: !userProfileIsOpen || !selectedUser
  });

  // Extract dialog fields safely
  const userNode = detailData?.user?.[0];
  const emailAddress = userNode?.email || "";
  const userCompanies = detailData?.userCompanies || [];

  // Only show companies where the user has access (state === true)
  const companiesWithAccess = (userCompanies || []).filter(c => !!c.state);

  return (
    <Container maxWidth={'xl'} disableGutters={true}>
      <Grid container alignItems="flex-start" justify="flex-end" direction="row">
        <Typography variant="h4" style={{ flex: 1 }}>Company Access Report</Typography>
      </Grid>

      <Paper className={classes.root} style={{ marginTop: '10px' }}>
        <TextField
          color="primary"
          variant="outlined"
          label="User Search"
          fullWidth
          style={{ marginTop: '10px' }}
          value={searchText}
          onChange={handleSearchTextChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleUserSearch();
          }}
        />
        {isLoading
          ? <CircularProgress />
          : <Button
              color="primary"
              variant="contained"
              style={{ marginTop: '10px', marginBottom: '10px' }}
              onClick={handleUserSearch}
            >
              Search
            </Button>
        }
      </Paper>

      {userSearchQueryResults?.data?.user &&
        <div style={{ paddingTop: '10px' }}>
          {userSearchQueryResults.data.user.length > 0 && userSearchQueryResults.data.user.map((item, index) => (
            <Card
              key={index}
              className={classes.fileCard}
              onClick={() => handleUserProfileOpen(item.guid)}
            >
              <CardContent>
                {item.email}
              </CardContent>
            </Card>
          ))}
          {userSearchQueryResults.data.user.length === 0 &&
            <Typography variant="subtitle1">No Users Found</Typography>
          }
        </div>
      }

      {/* Polished Dialog: email header + neat company list */}
      <Dialog
        open={userProfileIsOpen}
        onClose={handleDialogClose}
        style={{ overflow: 'hidden' }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className={classes.dialogTitle}>
          <div className={classes.titleRow}>
            <Typography variant="h5">Company Access Report</Typography>
          </div>
          <div className={classes.emailWrap}>
            <EmailIcon fontSize="small" />
            <Typography variant="body2">
              {emailAddress ? emailAddress.toLocaleLowerCase() : '—'}
            </Typography>
          </div>
          {/* <Typography variant="caption" className={classes.subtleCaption}>
            This is a read-only report view
          </Typography> */}
        </DialogTitle>

        <Divider className={classes.dividerTight} />

        <DialogContent className={classes.dialogContent}>
          {detailLoading ? (
            <CircularProgress />
          ) : (
            <>
              <div className={classes.sectionHeader}>
                <Typography variant="subtitle1">Companies with Access</Typography>
                <Chip
                  size="small"
                  color="primary"
                  label={companiesWithAccess.length}
                  variant="default"
                />
              </div>

              {companiesWithAccess.length > 0 ? (
                <List dense className={classes.companyList}>
                  {companiesWithAccess.map((item, idx) => (
                    <ListItem key={item?.company?.id ?? idx} className={classes.companyItem}>
                      <ListItemIcon>
                        <BusinessIcon className={classes.companyIcon} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item?.company?.name || '—'}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" className={classes.emptyState}>
                  No active company access.
                </Typography>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
